import { useAccountScope } from '../context/AccountScope';
import { useEffect, useState } from 'react';
import './MyAccountPage.css';

const roleNames = {
  super_admin: 'Superadministrador',
  admin_empresa: 'Empresa',
  administrador: 'Administrador',
  usuario: 'Usuario'
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function MyAccountPage({ onClose, onOpenAdministration,
  onOpenConfiguration, onViewVehicles }) {
  const { fetch, account: scopeAccount, isOwn } = useAccountScope();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/auth/my-account', { headers: authHeaders(), signal: controller.signal })
      .then(async response => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'No se pudo cargar Mi cuenta');
        return body;
      })
      .then(body => { setData(body); setName(body.account.name); })
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); });
    return () => controller.abort();
  }, []);

  async function submit(url, body, success) {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await fetch(url, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'No se pudo guardar');
      success(result);
    } catch (e) {
      if (e?.name === 'AbortError') return; setError(e.message); }
    finally { setBusy(false); }
  }

  function saveName(event) {
    event.preventDefault();
    submit('/api/auth/my-account/name', { name }, result => {
      setName(result.name);
      setData(previous => ({ ...previous,
        account: { ...previous.account, name: result.name } }));
      if (isOwn) {
        const local = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...local, name: result.name }));
      }
      window.dispatchEvent(new Event('maccez:profile-updated'));
      setNotice('Nombre actualizado.');
    });
  }

  function savePassword(event) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas nuevas no coinciden.'); return;
    }
    submit('/api/auth/my-account/password', { currentPassword, newPassword }, () => {
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setNotice('Contraseña actualizada. Úsala en tu próximo inicio de sesión.');
    });
  }

  const account = data?.account;
  return <main className="my-account-page">
    <header className="my-account-heading">
      <div><small>PERFIL PERSONAL</small><h1>{isOwn ? 'Mi cuenta' : 'Cuenta seleccionada'}</h1></div>
      <button type="button" onClick={onClose} aria-label="Volver al mapa">×</button>
    </header>

    {error && <p className="my-account-error" role="alert">{error}</p>}
    {notice && <p className="my-account-success" role="status">{notice}</p>}
    {!account && !error && <p role="status">Cargando tus datos…</p>}

    {account && <div className="my-account-grid">
      <section className="my-account-card">
        <h2>👤 Datos personales</h2>
        <form onSubmit={saveName}>
          <label>Nombre<input value={name} maxLength={100}
            onChange={event => setName(event.target.value)} required /></label>
          <button disabled={busy || name.trim() === account.name}>Guardar nombre</button>
        </form>
        <div className="my-account-info"><span>Correo de acceso</span><strong>{account.email}</strong></div>
        <div className="my-account-info"><span>Rol</span><strong>{roleNames[account.role] || account.role}</strong></div>
        {account.company_name && <div className="my-account-info">
          <span>Empresa</span><strong>{account.company_name}</strong>
        </div>}
        {account.parent_name && <div className="my-account-info">
          <span>Responsable</span><strong>{account.parent_name} · {roleNames[account.parent_role] || account.parent_role}</strong>
        </div>}
      </section>

      <section className="my-account-card">
        <h2>🔐 Seguridad</h2>
        <p>{isOwn ? 'Cambia la contraseña de tu cuenta.' : `Restablece la contraseña de ${scopeAccount.name}. Confirma con tu propia contraseña de acceso.`}</p>
        <form onSubmit={savePassword}>
          <label>{isOwn ? 'Contraseña actual' : 'Tu contraseña de acceso'}<input type="password" autoComplete="current-password"
            value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required /></label>
          <label>Nueva contraseña<input type="password" autoComplete="new-password"
            minLength={8} maxLength={128} value={newPassword}
            onChange={e => setNewPassword(e.target.value)} required /></label>
          <label>Confirma la nueva contraseña<input type="password" autoComplete="new-password"
            value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required /></label>
          <button disabled={busy}>{isOwn ? 'Cambiar contraseña' : 'Restablecer contraseña'}</button>
        </form>
      </section>

      <section className="my-account-card">
        <h2>🚗 Mis activos</h2>
        <p>{data.vehicles.length} {data.vehicles.length === 1 ? 'activo asociado' : 'activos asociados'} a tu cuenta.</p>
        <div className="my-account-vehicles">
          {data.vehicles.length ? data.vehicles.map(vehicle =>
            <div key={vehicle.id}><strong>{vehicle.name}</strong><span>{vehicle.plate || 'Sin placa'}</span></div>
          ) : <span>Aún no tienes activos a tu nombre.</span>}
        </div>
        <button onClick={onViewVehicles}>Abrir Activos</button>
      </section>

      {account.role === 'usuario' && data.availableLicenses > 0 &&
        <section className="my-account-card">
          <h2>📄 Licencias disponibles</h2>
          <p>{data.availableLicenses} para tus activos. Tu administrador puede asignarlas o renovarlas.</p>
        </section>}

      {account.role !== 'usuario' && <section className="my-account-card">
        <h2>🏢 Cuentas a cargo</h2>
        <p>{data.directReports.length} {data.directReports.length === 1 ? 'cuenta directa' : 'cuentas directas'}.</p>
        <div className="my-account-vehicles">
          {data.directReports.map(child => <div key={child.id}>
            <strong>{child.name}</strong><span>{roleNames[child.role] || child.role}</span>
          </div>)}
        </div>
        <button onClick={onOpenAdministration}>Abrir Administración</button>
      </section>}

      <section className="my-account-card">
        <h2>🔔 Preferencias</h2>
        <p>Consulta las preferencias de notificaciones de esta cuenta.</p>
        <button onClick={onOpenConfiguration}>Abrir Configuración</button>
      </section>
    </div>}
  </main>;
}
